import { supabase } from '../lib/supabase.js';
import { calcEventBudget } from '../utils/vypocet-mezd.js';

let settingsCache = null;
async function getSettings() {
    if (settingsCache) return settingsCache;
    const { data } = await supabase.from('settings').select('key, value');
    settingsCache = Object.fromEntries((data || []).map(s => [s.key, s.value]));
    return settingsCache;
}

function esc(s) { if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
function fmtKc(n, sign = '') {
    const v = Math.round(n || 0);
    const abs = Math.abs(v);
    const s = new Intl.NumberFormat('cs-CZ').format(abs);
    if (sign === '+' && abs > 0) return `+${s} Kč`;
    if (sign === '-' && abs > 0) return `– ${s} Kč`;
    return `${s} Kč`;
}
function fmtDate(d) {
    const dt = new Date(d);
    return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`;
}
const MONTHS = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

/**
 * Spočítá z events synthetický příjem = nájem aparatury + kapelnická mzda (do fondu).
 * Vrací entry-like objekt {entry_date, description, income, expense, category, synthetic}.
 */
function computeEventIncomes(events, settings) {
    return (events || []).filter(e => e.type === 'gig').map(ev => {
        const musicians = (ev.musicians || []).map(em => ({
            id: em.id, share: em.share_percent || 100, km: em.km || 0
        }));
        const budget = calcEventBudget({
            price: Number(ev.price_total) || 0,
            equipmentRent: Number(ev.equipment_rent) || 0,
            equipmentKm: Number(ev.equipment_km) || 0,
            foodAllowance: Number(ev.food_allowance) || 0,
            vanRate: Number(ev.rate_van_per_km) || Number(settings.rate_van_per_km) || 12,
            carRate: Number(ev.rate_car_per_km) || Number(settings.rate_car_per_km) || 8,
            targetBandWage: Number(ev.target_band_wage) || Number(settings.target_band_wage) || 500,
            roundingStep: Number(ev.rounding_step) || Number(settings.rounding_step) || 100,
            musicians
        });
        const today = new Date().toISOString().slice(0, 10);
        return {
            entry_date: ev.event_date,
            description: `${ev.place?.name || 'Akce'} – nájem aparatury + kapelnická mzda`,
            income: budget.toFund,
            expense: 0,
            category: 'akce',
            synthetic: true,
            planned: ev.event_date > today
        };
    }).filter(e => e.income > 0);
}

function renderMonthly(allEntries, year) {
    const months = Array.from({ length: 12 }, () => ({ income: 0, expense: 0, planned: false }));
    let cumul = 0;
    const rows = [];
    allEntries.filter(e => new Date(e.entry_date).getFullYear() === year)
        .forEach(e => {
            const m = new Date(e.entry_date).getMonth();
            months[m].income += e.income || 0;
            months[m].expense += e.expense || 0;
            if (e.planned) months[m].planned = true;
        });
    let totalIn = 0, totalEx = 0;
    months.forEach((m, i) => {
        if (m.income === 0 && m.expense === 0) return;
        const saldo = m.income - m.expense;
        cumul += saldo;
        totalIn += m.income; totalEx += m.expense;
        rows.push(`
            <tr${m.planned ? ' class="planned"' : ''}>
                <td>${MONTHS[i]} ${year}</td>
                <td class="num income-val">${m.income ? fmtKc(m.income, '+') : '0 Kč'}</td>
                <td class="num expense-val">${m.expense ? fmtKc(m.expense, '-') : '0 Kč'}</td>
                <td class="num ${saldo >= 0 ? 'income-val' : 'expense-val'}">${fmtKc(saldo, saldo >= 0 ? '+' : '-')}</td>
                <td class="num ${cumul >= 0 ? 'income-val' : 'expense-val'}">${fmtKc(cumul, cumul >= 0 ? '+' : '-')}</td>
            </tr>
        `);
    });
    return { rows: rows.join(''), totalIn, totalEx, saldo: totalIn - totalEx };
}

function renderBookRow(e) {
    const cat = e.category ? `<span class="cat-tag ${e.category}">${esc(catLabel(e.category))}</span> ` : '';
    return `
        <tr${e.planned ? ' class="planned"' : ''}>
            <td class="date">${fmtDate(e.entry_date)}</td>
            <td>${cat}${esc(e.description)}</td>
            <td class="num income-val">${e.income ? fmtKc(e.income, '+') : ''}</td>
            <td class="num expense-val">${e.expense ? fmtKc(e.expense, '-') : ''}</td>
            <td>${e.doc_path
                ? `<a href="#" class="doklad-link" data-path="${esc(e.doc_path)}">📎 ${esc(e.doc_path.split('/').pop())}</a>`
                : (e.synthetic ? '<span style="color:var(--text-muted);font-size:11px;">' + (e.planned ? 'po akci' : 'auto z akce') + '</span>' : '<span style="color:var(--text-muted);font-size:11px;">–</span>')
            }</td>
        </tr>
    `;
}

function catLabel(c) {
    return {
        'akce': 'Akce',
        'aparatura': 'Aparatura',
        'propagace': 'Propagace',
        'web': 'Web',
        'cloud': 'Cloud',
        'obleceni': 'Oblečení',
        'jine': 'Jiné'
    }[c] || c;
}

function openNewEntryModal(onSaved) {
    let m = document.getElementById('new-entry-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'new-entry-modal';
    m.className = 'modal-overlay open';
    const today = new Date().toISOString().slice(0, 10);
    m.innerHTML = `
        <div class="modal-window" style="max-width: 540px;">
            <div class="modal-head">
                <h2>Nový záznam do fondu</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <form id="ne-form">
                <div class="modal-body">
                    <div class="spec-section">
                        <div class="spec-row"><label>Datum</label><input type="date" name="entry_date" value="${today}" required></div>
                        <div class="spec-row" style="grid-template-columns: 1fr;">
                            <label>Opis (popis transakce)</label>
                        </div>
                        <div class="spec-row" style="grid-template-columns: 1fr;">
                            <input type="text" name="description" required placeholder="např. Servis mixáku Soundcraft" style="width:100%; padding:6px 10px; border:1px solid var(--border); border-radius:4px; font-family:inherit; font-size:14px;">
                        </div>
                        <div class="spec-row">
                            <label>Typ</label>
                            <select name="type">
                                <option value="expense" selected>Výdej</option>
                                <option value="income">Příjem</option>
                            </select>
                        </div>
                        <div class="spec-row">
                            <label>Kategorie</label>
                            <select name="category">
                                <option value="aparatura">Aparatura</option>
                                <option value="propagace">Propagace</option>
                                <option value="web">Web</option>
                                <option value="cloud">Cloud</option>
                                <option value="obleceni">Oblečení</option>
                                <option value="akce">Akce</option>
                                <option value="jine">Jiné</option>
                            </select>
                        </div>
                        <div class="spec-row"><label>Částka <span class="unit">Kč</span></label><input type="number" name="amount" required step="0.01" min="0"></div>
                        <div class="spec-row">
                            <label>Doklad <span class="unit">PDF / JPG (volitelné)</span></label>
                            <input type="file" name="doc" accept="image/*,application/pdf" style="font-size:12px;">
                        </div>
                    </div>
                    <p id="ne-error" style="color:var(--accent); font-size:13px; margin-top:10px; display:none;"></p>
                </div>
                <div class="modal-foot">
                    <button class="btn" type="button" id="ne-cancel">Zrušit</button>
                    <button class="btn btn-primary" type="submit">Uložit</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(m);

    const close = () => m.remove();
    m.querySelector('.modal-close').addEventListener('click', close);
    m.querySelector('#ne-cancel').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });

    m.querySelector('#ne-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        const errEl = m.querySelector('#ne-error');
        errEl.style.display = 'none';

        const type = fd.get('type');
        const amount = parseFloat(fd.get('amount')) || 0;
        const docFile = fd.get('doc');

        let doc_path = null;
        if (docFile && docFile.size > 0) {
            const ext = (docFile.name.split('.').pop() || 'pdf').toLowerCase();
            const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
            const { error: uerr } = await supabase.storage.from('documents').upload(path, docFile, {
                cacheControl: '3600', upsert: false, contentType: docFile.type
            });
            if (uerr) {
                errEl.textContent = 'Chyba uploadu dokladu: ' + uerr.message + ' (jdi do Storage v Supabase Studio a vytvoř bucket "documents", nebo spusť supabase/storage.sql)';
                errEl.style.display = 'block';
                return;
            }
            doc_path = path;
        }

        const { data: { user } } = await supabase.auth.getUser();
        const insert = {
            entry_date: fd.get('entry_date'),
            description: fd.get('description').trim(),
            income: type === 'income' ? amount : 0,
            expense: type === 'expense' ? amount : 0,
            category: fd.get('category'),
            doc_path,
            created_by: user?.id
        };
        const { error } = await supabase.from('fund_entries').insert(insert);
        if (error) {
            errEl.textContent = 'Chyba uložení: ' + error.message;
            errEl.style.display = 'block';
            return;
        }
        close();
        onSaved?.();
    });
}

export async function renderFond(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';
    const settings = await getSettings();

    const [{ data: events }, { data: dbEntries, error }] = await Promise.all([
        supabase.from('events').select(`
            *,
            place:places(name),
            musicians:event_musicians(id, share_percent, km)
        `).eq('type', 'gig').order('event_date'),
        supabase.from('fund_entries').select('*').order('entry_date', { ascending: false })
    ]);

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(error.message)}</div>`;
        return;
    }

    const synthIncomes = computeEventIncomes(events, settings);
    const all = [...(dbEntries || []), ...synthIncomes]
        .sort((a, b) => b.entry_date.localeCompare(a.entry_date));

    const currentYear = new Date().getFullYear();
    const yearTotals = all
        .filter(e => new Date(e.entry_date).getFullYear() === currentYear)
        .reduce((acc, e) => ({
            income: acc.income + (e.income || 0),
            expense: acc.expense + (e.expense || 0)
        }), { income: 0, expense: 0 });
    const balance = yearTotals.income - yearTotals.expense;
    const monthly = renderMonthly(all, currentYear);

    container.innerHTML = `
        <div class="filters">
            <select class="filter-select" style="margin-right:auto;"><option>Rok ${currentYear}</option></select>
            <button class="btn btn-sm" disabled>Export CSV</button>
            <button class="btn btn-sm btn-primary" id="new-entry-btn">+ Nový záznam</button>
        </div>

        <div class="accounting-section">
            <table class="accounting-table fund-book">
                <thead><tr>
                    <th>Datum</th>
                    <th>Opis</th>
                    <th class="num">Příjem</th>
                    <th class="num">Výdej</th>
                    <th>Doklad</th>
                </tr></thead>
                <tbody>${all.map(renderBookRow).join('') || '<tr><td colspan="5" style="text-align:center;padding:30px;color:var(--text-muted);">Kniha je prázdná.</td></tr>'}</tbody>
                <tfoot>
                    <tr>
                        <td colspan="2">Celkem ${currentYear}</td>
                        <td class="num income-val">${fmtKc(yearTotals.income, '+')}</td>
                        <td class="num expense-val">${fmtKc(yearTotals.expense, '-')}</td>
                        <td class="saldo" style="background:var(--accent-soft); color:var(--accent);">Saldo: ${fmtKc(balance)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>

        ${monthly.rows ? `
        <div class="accounting-section">
            <div class="section-head">
                <div>
                    <h3>Měsíční závěrky ${currentYear}</h3>
                    <div class="hint">Součty + kumulativní stav fondu.</div>
                </div>
            </div>
            <table class="accounting-table">
                <thead><tr>
                    <th>Měsíc</th>
                    <th class="num">Příjmy</th>
                    <th class="num">Výdaje</th>
                    <th class="num">Saldo měsíce</th>
                    <th class="num">Stav fondu</th>
                </tr></thead>
                <tbody>${monthly.rows}</tbody>
                <tfoot>
                    <tr>
                        <td>Roční závěrka ${currentYear}</td>
                        <td class="num income-val">${fmtKc(monthly.totalIn, '+')}</td>
                        <td class="num expense-val">${fmtKc(monthly.totalEx, '-')}</td>
                        <td class="num ${monthly.saldo >= 0 ? 'income-val' : 'expense-val'}">${fmtKc(monthly.saldo, monthly.saldo >= 0 ? '+' : '-')}</td>
                        <td class="num" style="background:var(--accent-soft); color:var(--accent);">Stav: ${fmtKc(monthly.saldo)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
        ` : ''}

        <div class="footer-note">
            <strong>Logika fondu:</strong> z každé akce se do fondu přepočítá <code>nájem aparatury + kapelnická mzda</code>.
            Záznamy s tagem <em>plán</em> jsou nadcházející akce. Výdaje přidává administrátor přes <strong>+ Nový záznam</strong>.
        </div>
    `;

    container.querySelector('#new-entry-btn').addEventListener('click', () => {
        openNewEntryModal(() => renderFond(container));
    });
}
